import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { BasilPackage } from "@/components/icons/basil";
import { cn } from "@shared/lib/utils";
import { StatusBadge } from "@/components/renewal/system";
import { usePrefersReducedMotion } from "@/components/home/usePrefersReducedMotion";

/**
 * Courtyard "Just Pulled" 레일(pages/home.md §2) — 헤더 1행(타이틀 24/700 +
 * 출처 캡션) → 가로 스크롤 스냅 레일(양끝 24px mask, 스크롤바 숨김).
 * 타일 문법(위→아래 고정): ①이미지 패널(bg-3 위 p-2 카드 슬랩) → ②이름 2줄
 * 14/700 → ③구분선 → ④하단 행 [팩 아이콘+팩명 · 상대시간] 또는 stat 단독.
 * 번호 뱃지 오버랩·자동 스크롤 없음(§5-5)·레일 전체 `to` 링크는 기존 유지.
 * `live` — 라이브 피드 모드: 새 타일이 좌측에서 스프링 진입, 기존 타일은
 * layout 트랜지션으로 밀려난다(reduced-motion 시 정적 렌더).
 */
export type RankRailTile = {
  key: string;
  /** 슬랩 내용물 — 슬랩 컨테이너가 relative라 절대 배치 가능 */
  image: ReactNode;
  name: string;
  /** 하단 행 수치 — mono tnum 골드. source 지정 시 우측 상대시간 슬롯 */
  stat: string;
  /** 출처 팩명 — 지정 시 하단 행이 [팩 아이콘+팩명 · stat(시간)]으로 렌더 */
  source?: string;
  /** 획득 완료 — 명도 강등 + Claimed 뱃지(제거 금지) */
  claimed?: boolean;
  /** 개별 목적지 — 미지정 시 레일 `to` */
  to?: string;
  /** 순번 뱃지 생략 — 랭킹이 아닌 엔트리 타일용 */
  unranked?: boolean;
};

const TILE_FOCUS =
  "outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent/70";

const SLOT_CLASS = "w-[140px] shrink-0 snap-start lg:w-[168px]";

function Tile({ tile, index, to }: { tile: RankRailTile; index: number; to: string }) {
  return (
    <Link to={tile.to ?? to} className={cn("relative block w-full", TILE_FOCUS)}>
      {/* 번호 뱃지 — 좌상단 오버랩, 원형 24px 골드 헤어라인 */}
      {!tile.unranked && (
        <span
          aria-hidden
          className="absolute -left-2 -top-2 z-10 grid size-6 place-items-center rounded-full border border-(--ty-line-gold) bg-(--ty-bg) font-mono text-[11px] font-medium tabular-nums text-accent"
        >
          {index + 1}
        </span>
      )}
      {/* ① 이미지 패널 — 밝은 패널(bg-3) 위 카드 슬랩(p-2 인셋) */}
      <span className="relative block aspect-[723/1024] rounded-[12px] border border-(--ty-line-gold) bg-(--ty-bg-3) p-2">
        <span
          className={cn(
            "relative block h-full w-full overflow-hidden rounded-[8px] bg-(--ty-bg-2)",
            tile.claimed && "opacity-55",
          )}
        >
          {tile.image}
        </span>
        {tile.claimed && (
          <StatusBadge tone="neutral" className="absolute right-1.5 top-1.5 bg-(--ty-bg-3)">
            Claimed
          </StatusBadge>
        )}
      </span>
      {/* ② 이름 2줄 — 14/700, 2줄 높이 상시 확보 */}
      <span
        className={cn(
          "mt-2 line-clamp-2 block min-h-[2.7em] text-sm font-bold leading-[1.35]",
          tile.claimed ? "text-ink-dim" : "text-ink",
        )}
      >
        {tile.name}
      </span>
      {/* ③ 구분선 */}
      <span aria-hidden className="my-1.5 block border-t border-line" />
      {/* ④ 하단 행 — [팩 아이콘+팩명 · 시간] 또는 stat 단독(mono tnum) */}
      {tile.source ? (
        <span className="flex items-center justify-between gap-2">
          <span className="flex min-w-0 items-center gap-1 text-[11px] text-ink-mute">
            <BasilPackage className="size-3 shrink-0" aria-hidden />
            <span className="truncate">{tile.source}</span>
          </span>
          <span className="shrink-0 font-mono text-[11px] font-medium tabular-nums text-ink-mute">
            {tile.stat}
          </span>
        </span>
      ) : (
        <span className="block truncate font-mono text-xs font-medium tabular-nums text-accent">
          {tile.stat}
        </span>
      )}
    </Link>
  );
}

export function RankRail({
  ariaLabel,
  to,
  tiles,
  live,
}: {
  /** 섹션 접근성 라벨 — 시각 제목은 호출부 눈썹(#n)이 담당(부제목 제거). */
  ariaLabel: string;
  /** 레일 전체 클릭 목적지 */
  to: string;
  tiles: RankRailTile[];
  /** 라이브 피드 모드 — 신규 타일 진입 애니메이션 (reduced-motion 시 무시) */
  live?: boolean;
}) {
  const reduced = usePrefersReducedMotion();
  const animated = live === true && !reduced;

  const rendered = tiles.map((tile, index) =>
    animated ? (
      <motion.div
        key={tile.key}
        layout
        initial={{ opacity: 0, x: -28, scale: 0.94 }}
        animate={{ opacity: 1, x: 0, scale: 1 }}
        exit={{ opacity: 0, scale: 0.94 }}
        transition={{ type: "spring", stiffness: 320, damping: 32 }}
        className={SLOT_CLASS}
      >
        <Tile tile={tile} index={index} to={to} />
      </motion.div>
    ) : (
      <div key={tile.key} className={SLOT_CLASS}>
        <Tile tile={tile} index={index} to={to} />
      </div>
    ),
  );

  return (
    <section aria-label={ariaLabel}>
      <div
        className={cn(
          // px-8/scroll-pl-8 — 순번 뱃지·마지막 타일이 24px 마스크 페이드에 안 먹힌다(improvements §M-18)
          "flex snap-x snap-mandatory gap-4 overflow-x-auto px-8 pb-1 pt-2 scroll-pl-8",
          "[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          "[mask-image:linear-gradient(to_right,transparent,black_24px,black_calc(100%-24px),transparent)]",
        )}
      >
        {animated ? (
          <AnimatePresence initial={false} mode="popLayout">
            {rendered}
          </AnimatePresence>
        ) : (
          rendered
        )}
      </div>
    </section>
  );
}
